import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  ArrowRight as ArrowRightIcon,
  Envelope as EmailIcon,
  Phone as PhoneIcon,
  ShieldCheck as ShieldIcon,
  User as UserIcon,
} from '@phosphor-icons/react';
import type { PublicMailerClaimRequest } from '../../../types/directMail';
import type { BrandPalette } from './brand';

interface MailerClaimFormProps {
  palette: BrandPalette;
  submitting: boolean;
  error: string | null;
  onSubmit: (request: PublicMailerClaimRequest) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function MailerClaimForm({ palette, submitting, error, onSubmit }: MailerClaimFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.first_name = 'Please tell us your name';
    if (!email.trim()) {
      errors.email = 'We need an email to send your estimate';
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      errors.email = "That email address doesn't look right";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      message: message.trim(),
    });
  };

  return (
    <Card variant="outlined" sx={{ borderColor: palette.border, boxShadow: 'sm' }}>
      <Box>
        <Typography level="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', sm: '1.6rem' } }}>
          Claim this estimate
        </Typography>
        <Typography level="body-sm" textColor="text.secondary" sx={{ mt: 0.5 }}>
          Tell us where to reach you and we'll confirm the details. No pressure, no obligation.
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl required error={!!fieldErrors.first_name} sx={{ flex: 1 }}>
              <FormLabel>First name</FormLabel>
              <Input
                size="lg"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                autoComplete="given-name"
                placeholder="Jamie"
                startDecorator={<UserIcon size={18} />}
              />
              {fieldErrors.first_name && <FormHelperText>{fieldErrors.first_name}</FormHelperText>}
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Last name</FormLabel>
              <Input
                size="lg"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                autoComplete="family-name"
                placeholder="Rivera"
              />
            </FormControl>
          </Stack>

          <FormControl required error={!!fieldErrors.email}>
            <FormLabel>Email</FormLabel>
            <Input
              size="lg"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              inputMode="email"
              placeholder="you@email.com"
              startDecorator={<EmailIcon size={18} />}
            />
            {fieldErrors.email && <FormHelperText>{fieldErrors.email}</FormHelperText>}
          </FormControl>

          <FormControl>
            <FormLabel>Phone</FormLabel>
            <Input
              size="lg"
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              autoComplete="tel"
              inputMode="tel"
              placeholder="(555) 123-4567"
              startDecorator={<PhoneIcon size={18} />}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Anything we should know?</FormLabel>
            <Textarea
              size="lg"
              minRows={3}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Leaks, missing shingles, insurance claim, best time to call…"
            />
          </FormControl>

          {error && <Alert color="danger">{error}</Alert>}

          <Button
            type="submit"
            size="lg"
            loading={submitting}
            endDecorator={<ArrowRightIcon size={20} weight="bold" />}
            sx={{
              minHeight: 56,
              fontSize: '1.05rem',
              fontWeight: 700,
              bgcolor: palette.base,
              color: palette.onBase,
              '&:hover': { bgcolor: palette.base, filter: 'brightness(0.92)' },
            }}
          >
            Get my estimate
          </Button>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Box sx={{ color: 'text.tertiary', display: 'flex' }}>
              <ShieldIcon size={16} />
            </Box>
            <Typography level="body-xs" textColor="text.tertiary">
              Your details go straight to our office. We never sell them.
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
}
